/**
 * PATH: packages/ui/src/kanban/card-surface/projections/CardTileSurface.tsx
 * FILE_ID: UI-KANBAN-CARDSURFACE-TILE-001
 * PURPOSE: Adapt the canonical Kanban tile to the board-scoped card runtime.
 * WHY: Flat Board must consume the new framework without changing existing tile behavior.
 * FLOW: legacy Card props + optional runtime → canonical tile + surface metadata.
 * DEPENDENCIES: KanbanCard; CardCapabilityProvider.
 * RELATED: components/lane/LaneCardItem.tsx; runtime/CardCapabilityProvider.tsx.
 */
import { memo } from 'react'

import { KanbanCard } from '../../components/KanbanCard'
import type { KanbanCardProps } from '../../components/KanbanCard.types'
import type { Lane } from '../../types/lane'
import type {
  CardCommandId,
  CardCommandInput,
  CardSurfaceId,
  CardSurfaceRuntime,
} from '../contract'
import { useOptionalCardSurfaceRuntime } from '../runtime/useCardCapabilityRuntime'

export interface CardTileSurfaceProps extends KanbanCardProps {
  surfaceId?: CardSurfaceId
}

function executeCardCommand(
  runtime: CardSurfaceRuntime | null | undefined,
  commandId: CardCommandId,
  card: KanbanCardProps['card'],
  surfaceId: CardSurfaceId,
  input: CardCommandInput = {},
) {
  const command = runtime?.commands[commandId]
  if (command?.availability !== 'available') return undefined
  return command.execute({ card, surfaceId, ...input })
}

export const CardTileSurface = memo(function CardTileSurface({
  surfaceId,
  cardSurfaceId,
  ...props
}: CardTileSurfaceProps) {
  const runtime = useOptionalCardSurfaceRuntime()
  // LaneCardItem predates this adapter and supplies the canonical
  // `KanbanCardProps.cardSurfaceId`. Accept both names while preferring the
  // adapter-specific one so alternate tiles retain their registry identity.
  const effectiveSurfaceId = surfaceId ?? cardSurfaceId ?? 'board-lane-card'
  const execute = (
    commandId: CardCommandId,
    card: KanbanCardProps['card'],
    input?: CardCommandInput,
  ) => executeCardCommand(runtime, commandId, card, effectiveSurfaceId, input)
  const hasRuntimeCommand = (commandId: CardCommandId) =>
    runtime?.commands[commandId].availability === 'available'
  const quickActions = props.quickActions
    ? {
        ...props.quickActions,
        onSetPriority:
          hasRuntimeCommand('set-priority') || props.quickActions.onSetPriority
            ? (card: KanbanCardProps['card'], priority: number) =>
                execute('set-priority', card, { priority }) ??
                props.quickActions?.onSetPriority?.(card, priority)
            : undefined,
        onSetActionDate:
          hasRuntimeCommand('set-action-date') || props.quickActions.onSetActionDate
            ? (card: KanbanCardProps['card'], date: string | null) =>
                execute('set-action-date', card, { date }) ??
                props.quickActions?.onSetActionDate?.(card, date)
            : undefined,
        onSetDueDate:
          hasRuntimeCommand('set-due-date') || props.quickActions.onSetDueDate
            ? (card: KanbanCardProps['card'], date: string | null) =>
                execute('set-due-date', card, { date }) ??
                props.quickActions?.onSetDueDate?.(card, date)
            : undefined,
        onAssign:
          hasRuntimeCommand('assign') || props.quickActions.onAssign
            ? (
                card: KanbanCardProps['card'],
                userId: number,
                assign: boolean,
              ) =>
                execute('assign', card, { userId, assign }) ??
                props.quickActions?.onAssign?.(card, userId, assign)
            : undefined,
        onMoveTo:
          hasRuntimeCommand('move') || props.quickActions.onMoveTo
            ? (card: KanbanCardProps['card'], lane: Lane) =>
                execute('move', card, { lane }) ??
                props.quickActions?.onMoveTo?.(card, lane)
            : undefined,
        onToggleArchive:
          hasRuntimeCommand('archive') || props.quickActions.onToggleArchive
            ? (card: KanbanCardProps['card']) =>
                execute('archive', card) ?? props.quickActions?.onToggleArchive?.(card)
            : undefined,
        onOpenWorkbook:
          hasRuntimeCommand('open-workbook') || props.quickActions.onOpenWorkbook
            ? (card: KanbanCardProps['card']) => {
                const result = execute('open-workbook', card)
                if (result) void result
                else props.quickActions?.onOpenWorkbook?.(card)
              }
            : undefined,
        onOpenFlowchart:
          hasRuntimeCommand('open-flowchart') || props.quickActions.onOpenFlowchart
            ? (card: KanbanCardProps['card']) => {
                const result = execute('open-flowchart', card)
                if (result) void result
                else props.quickActions?.onOpenFlowchart?.(card)
              }
            : undefined,
        onOpenEntries:
          hasRuntimeCommand('open-entries') || props.quickActions.onOpenEntries
            ? (card: KanbanCardProps['card']) => {
                const result = execute('open-entries', card)
                if (result) void result
                else props.quickActions?.onOpenEntries?.(card)
              }
            : undefined,
      }
    : props.quickActions
  const openHandler =
    hasRuntimeCommand('open') || props.onOpen
      ? (card: KanbanCardProps['card']) => {
          const result = execute('open', card)
          if (result) void result
          else props.onOpen?.(card)
        }
      : undefined
  const contextMenuHandler =
    hasRuntimeCommand('open-context-menu') || props.onContextMenu
      ? (card: KanbanCardProps['card'], anchor: { x: number; y: number }) => {
          const result = execute('open-context-menu', card, { anchor })
          if (result) void result
          else props.onContextMenu?.(card, anchor)
        }
      : undefined
  const toggleCompleteHandler =
    hasRuntimeCommand('toggle-complete') || props.onToggleComplete
      ? (card: KanbanCardProps['card']) =>
          execute('toggle-complete', card) ?? props.onToggleComplete?.(card)
      : undefined
  const renameHandler =
    hasRuntimeCommand('rename') || props.onRenameInline
      ? (card: KanbanCardProps['card'], title: string) =>
          execute('rename', card, { title }) ?? props.onRenameInline?.(card, title)
      : undefined
  const openEmailHandler =
    hasRuntimeCommand('open-email') || props.onOpenEmail
      ? (card: KanbanCardProps['card']) => {
          const result = execute('open-email', card)
          if (result) void result
          else props.onOpenEmail?.(card)
        }
      : undefined

  return (
    <KanbanCard
      {...props}
      onOpen={openHandler}
      onContextMenu={contextMenuHandler}
      onToggleComplete={toggleCompleteHandler}
      onRenameInline={renameHandler}
      onOpenEmail={openEmailHandler}
      quickActions={quickActions}
      cardSurfaceId={effectiveSurfaceId}
      cardSurfaceMode={runtime?.mode}
    />
  )
})
